import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchAspsps, connectAspsp, type AspspInfo } from '../services/aspsp';

/** Country code to flag emoji */
function countryFlag(code: string): string {
  const upper = code.toUpperCase();
  if (upper.length !== 2) return '';
  const a = 0x1f1e6;
  return String.fromCodePoint(
    a + upper.charCodeAt(0) - 65,
    a + upper.charCodeAt(1) - 65,
  );
}

export default function BanksPage() {
  const [aspsps, setAspsps] = useState<AspspInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle callback query params (connected / error)
  const connectedAspsp = searchParams.get('connected');
  const callbackError = searchParams.get('error');

  const loadAspsps = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchAspsps();
      setAspsps(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load banks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAspsps();
  }, [loadAspsps]);

  // Clear callback params after showing notification
  const dismissNotification = () => {
    setSearchParams({}, { replace: true });
  };

  const handleConnect = async (aspspId: string) => {
    setConnecting(aspspId);
    setError(null);
    try {
      const { authorization_url } = await connectAspsp(aspspId);
      // Redirect the user to the bank's authorization page
      window.location.href = authorization_url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to connect');
      setConnecting(null);
    }
  };

  const filtered = aspsps.filter((a) => {
    const q = search.toLowerCase();
    return (
      a.name.toLowerCase().includes(q) ||
      a.country.toLowerCase().includes(q) ||
      (a.bic && a.bic.toLowerCase().includes(q))
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Banks</h1>
        <button
          onClick={() => void loadAspsps()}
          disabled={loading}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Success notification */}
      {connectedAspsp && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-4 flex items-center justify-between">
          <p className="text-sm text-green-800">
            Successfully connected to <strong>{connectedAspsp}</strong>
          </p>
          <button
            onClick={dismissNotification}
            className="text-green-600 hover:text-green-800 text-sm font-medium"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Error notification from callback */}
      {callbackError && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 flex items-center justify-between">
          <p className="text-sm text-red-700">{callbackError}</p>
          <button
            onClick={dismissNotification}
            className="text-red-600 hover:text-red-800 text-sm font-medium"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Error from API call */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Search */}
      <div>
        <input
          type="text"
          placeholder="Search banks by name, country, or BIC..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
        />
      </div>

      {/* Bank cards */}
      {loading && aspsps.length === 0 ? (
        <div className="text-center py-12">
          <svg
            className="animate-spin text-primary-600 mx-auto"
            style={{ width: '32px', height: '32px' }}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="mt-3" style={{ fontSize: '14px', color: '#6b7280' }}>Loading available banks...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-sm text-gray-500">
            {aspsps.length === 0
              ? 'No banks configured yet.'
              : 'No banks match your search.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((aspsp) => (
            <div
              key={aspsp.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col justify-between hover:border-primary-300 transition-colors"
            >
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xl" role="img" aria-label={aspsp.country}>
                    {countryFlag(aspsp.country)}
                  </span>
                  <h3 className="text-base font-semibold text-gray-900">{aspsp.name}</h3>
                </div>
                {aspsp.legal_name && aspsp.legal_name !== aspsp.name && (
                  <p className="text-xs text-gray-500">{aspsp.legal_name}</p>
                )}
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span className="uppercase">{aspsp.country}</span>
                  {aspsp.bic && (
                    <>
                      <span className="text-gray-300">|</span>
                      <span className="font-mono">{aspsp.bic}</span>
                    </>
                  )}
                </div>
                <div className="flex gap-2 mt-1">
                  {aspsp.supports_ais && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                      AIS
                    </span>
                  )}
                  {aspsp.supports_pis && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700">
                      PIS
                    </span>
                  )}
                  {aspsp.has_sandbox && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700">
                      Sandbox
                    </span>
                  )}
                </div>
              </div>

              {aspsp.status !== 'apocryphal' ? (
                <button
                  onClick={() => void handleConnect(aspsp.id)}
                  disabled={connecting !== null}
                  className="mt-4 w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {connecting === aspsp.id ? 'Connecting...' : 'Connect'}
                </button>
              ) : (
                <p className="mt-4 text-center text-xs text-gray-400">Coming soon</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
